import type { AvatarConfig } from './types'
import { LAST_CALL_FRAME_ID, LAST_CALL_PORTRAIT_ID } from '../events/lastCall'
import { LEAGUE_FRAME_IDS } from '../cosmetics/leagueRewards'
import { AVATAR_FRAME_IDS, DEFAULT_FRAME_ID, type AvatarFrameId } from './frames'

/**
 * Cards offered as profile portraits (artwork window only): twenty from LOB,
 * then twenty from MRD.
 */
export const AVATAR_CARD_IDS: readonly string[] = [
  '89631139', // Blue-Eyes White Dragon
  '36996508', // Dark Magician
  '74677422', // Red-Eyes Black Dragon
  '33396948', // Exodia the Forbidden One
  '91152256', // Celtic Guardian
  '6368038', // Gaia The Fierce Knight
  '28279543', // Curse of Dragon
  '45231177', // Flame Swordsman
  '15025844', // Mystical Elf
  '76184692', // Hitotsu-Me Giant
  '13039848', // Giant Soldier of Stone
  '90357090', // Silver Fang
  '32274490', // Skull Servant
  '54652250', // Man-Eater Bug
  '53129443', // Dark Hole
  '12580477', // Raigeki
  '83764718', // Monster Reborn
  '72302403', // Swords of Revealing Light
  '55144522', // Pot of Greed
  '33066139', // Reaper of the Cards
  '11901678', // Black Skull Dragon
  '25833572', // Gate Guardian
  '62340868', // Kazejin
  '98434877', // Suijin
  '25955164', // Sanga of the Thunder
  '41462083', // Thousand Dragon
  '71625222', // Time Wizard
  '88819587', // Baby Dragon
  '76812113', // Harpie Lady
  '12206212', // Harpie Lady Sisters
  '40640057', // Kuriboh
  '81480460', // Barrel Dragon
  '78010363', // Witch of the Black Forest
  '26202165', // Sangan
  '14141448', // Great Moth
  '95727991', // Catapult Turtle
  '54752875', // Twin-Headed Thunder Dragon
  '44095762', // Mirror Force
  '19613556', // Heavy Storm
  '41420027', // Solemn Judgment
]

export const DEFAULT_AVATAR: AvatarConfig = {
  cardId: AVATAR_CARD_IDS[0]!,
  frameId: DEFAULT_FRAME_ID,
}

const CPU_LOOKS: readonly { cardId: string; frameId: AvatarFrameId }[] = [
  { cardId: AVATAR_CARD_IDS[2]!, frameId: 'crimson' },
  { cardId: AVATAR_CARD_IDS[1]!, frameId: 'obsidian' },
  { cardId: AVATAR_CARD_IDS[5]!, frameId: 'bronze' },
  { cardId: AVATAR_CARD_IDS[19]!, frameId: 'emerald' },
]

export const CPU_AVATARS: readonly AvatarConfig[] = CPU_LOOKS.map((look) => ({
  ...look,
}))

/** Portrait reserved for the account named crx. Not a card passcode. */
export const EXCLUSIVE_AVATAR_OWNER = 'crx'
export const EXCLUSIVE_AVATAR_CARD_ID = 'exclusive-crx'

/**
 * Public image for {@link EXCLUSIVE_AVATAR_CARD_ID}.
 * Append `?v=` with {@link AvatarConfig.rev} so a new upload is not cached.
 */
export const EXCLUSIVE_PORTRAIT_PATH = '/avatars/exclusive'

const ALLOWED = new Set(AVATAR_CARD_IDS)

export function isExclusiveAvatarCard(id: string): boolean {
  return id === EXCLUSIVE_AVATAR_CARD_ID
}

/** Case-insensitive: login already treats Crx and crx as the same name. */
export function usernameOwnsExclusiveAvatar(username: string): boolean {
  return username.trim().toLowerCase() === EXCLUSIVE_AVATAR_OWNER
}

/**
 * Same-origin scan used to crop the artwork window.
 */
export function avatarCardSrc(cardId: string): string {
  return `/card-images/${cardId}.jpg`
}

/**
 * Keep a listed card or the crx portrait; otherwise Blue-Eyes.
 * Who may wear the portrait is decided in {@link parseAvatarForUser}.
 */
export function coerceAvatarCardId(id: string): string {
  if (
    isExclusiveAvatarCard(id) ||
    id === LAST_CALL_PORTRAIT_ID ||
    ALLOWED.has(id)
  ) return id
  return DEFAULT_AVATAR.cardId
}

/**
 * Seeded CPU rival so vs-AI is not a clone of the player.
 */
export function pickCpuAvatar(seed: number): AvatarConfig {
  const i = Math.abs(seed >>> 0) % CPU_AVATARS.length
  return { ...CPU_AVATARS[i]! }
}

/**
 * A random portrait from the listed cards (keeps a random frame).
 */
export function randomAvatar(rng: () => number = Math.random): AvatarConfig {
  const i = Math.floor(rng() * AVATAR_CARD_IDS.length) % AVATAR_CARD_IDS.length
  const frames = AVATAR_FRAME_IDS.filter(
    (id) => id !== LAST_CALL_FRAME_ID && !LEAGUE_FRAME_IDS.includes(id),
  )
  const f = Math.floor(rng() * frames.length) % frames.length
  return {
    cardId: AVATAR_CARD_IDS[i]!,
    frameId: frames[f]!,
  }
}
